import { useMemo, useState } from "react";
import { PageLayout } from "../../Components/PageLayout";
import { DataTable } from "../../Components/ui/table";
import { DataTableColumn } from "../../Components/ui/table/types";
import { CursorPagination } from "../../Components/ui/table/pagination";
import { FiltersWrapper } from "../../Components/ui/table/filters";
import { InputFilter } from "../../Components/ui/table/filters/input";
import { usePaginatedQuery } from "../../Hooks/use-paginated-query";
import { Button } from "../../Components/ui/button";
import { CreateSupplierForm } from "./create-supplier";
import { useNavigate } from "react-router-dom";

type SupplierRow = {
  id: number;
  name: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  country: string;
  currency: "USD" | "MXN";
  phone: string;
  email: string;
};

const columns: DataTableColumn<SupplierRow>[] = [
  {
    header: "Name",
    labelKey: "name",
    field: "name",
    sortable: true,
  },
  {
    header: "Address",
    labelKey: "address",
    field: "address",
    sortable: true,
  },
  {
    header: "City",
    labelKey: "city",
    field: "city",
    sortable: true,
  },
  {
    header: "State",
    labelKey: "state",
    field: "state",
    sortable: true,
  },
  {
    header: "Zip",
    labelKey: "zip",
    field: "zip_code",
    sortable: true,
  },
  {
    header: "Country",
    labelKey: "country",
    field: "country",
    sortable: true,
  },
  {
    header: "Currency",
    labelKey: "currency",
    field: "currency",
    sortable: true,
  },
  {
    header: "Phone",
    labelKey: "phone",
    field: "phone",
    sortable: true,
  },
  {
    header: "Email",
    labelKey: "email",
    field: "email",
    sortable: true,
  },
];

const initialUrl = "/safety/suppliers";

const SuppliersRoute = () => {
  const navigate = useNavigate();
  const [nameFilter, setNameFilter] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [stateFilter, setStateFilter] = useState("");

  const [createModalOpen, setCreateModalOpen] = useState(false);

  const params = useMemo(
    () => ({
      ...(nameFilter.trim() ? { name: nameFilter.trim() } : {}),
      ...(cityFilter.trim() ? { city: cityFilter.trim() } : {}),
      ...(stateFilter.trim() ? { state: stateFilter.trim() } : {}),
    }),
    [nameFilter, cityFilter, stateFilter],
  );

  const results = usePaginatedQuery<SupplierRow>({
    initialUrl,
    params,
  });

  return (
    <PageLayout title="Suppliers" header="suppliers">
      {createModalOpen && (
        <CreateSupplierForm cancel={() => setCreateModalOpen(false)} />
      )}
      <FiltersWrapper>
        <InputFilter
          value={nameFilter}
          onChange={setNameFilter}
          placeholder="name"
        />
        <InputFilter
          value={cityFilter}
          onChange={setCityFilter}
          placeholder="city"
        />
        <InputFilter
          value={stateFilter}
          onChange={setStateFilter}
          placeholder="state"
        />
        <Button
          variant="positive"
          labelKey="create"
          onClick={() => setCreateModalOpen(true)}
        />
      </FiltersWrapper>
      <CursorPagination
        hasNext={results.hasNext}
        hasPrevious={results.hasPrevious}
        currentPage={results.currentPage}
        fetchNextPage={results.fetchNextPage}
        fetchPreviousPage={results.fetchPreviousPage}
      />
      <DataTable
        columns={columns}
        isLoading={results.isLoading}
        data={results.results ?? []}
        onRowClick={(row) => navigate(`/suppliers/${row.id}`)}
        getRowKey={(row) => row.id.toString()}
      />
    </PageLayout>
  );
};

export default SuppliersRoute;
